import PKCELib from 'js-pkce';
import appConfig from "@/config/app.config";
import axios from "axios";
import {useUsersStore} from "@/stores/Users.store";
import {FilePickerCredentials} from "@/utils/FilePickerCredentials.util";

const PKCE = PKCELib.default ?? PKCELib;

// Refresh slightly before actual expiry to avoid in-flight requests racing the deadline.
const PROACTIVE_REFRESH_LEAD_MS = 60 * 1000;

export class Authentication {

	authenticated = false;

	accessTokenCallbacks = [];

	// Single in-flight refresh promise, shared by all concurrent callers.
	_refreshInFlight = null;

	// Timer handle for proactive refresh.
	_proactiveRefreshTimer = null;

	constructor() {
		// setTimeout is unreliable across laptop sleep and long tab-backgrounded
		// periods — a scheduled proactive refresh can be skipped entirely. When
		// the tab regains visibility, recompute the schedule from the stored
		// expiry. If the token has already expired, _scheduleProactiveRefresh
		// resolves to delay = 0 and fires the refresh immediately.
		if (typeof document !== 'undefined') {
			document.addEventListener('visibilitychange', () => {
				if (document.visibilityState === 'visible' && localStorage.getItem('access_token')) {
					this._scheduleProactiveRefresh();
				}
			});
		}
	}

	async authenticate() {

		if (this.authenticated) {
			return useUsersStore().me;
		}

		if (!await this.requireAccessToken()) {
			return;
		}

		try {
			const user = await useUsersStore().loadMe();
			if (user.locale) {
				let originalLocalStorage = localStorage.getItem('locale');
				localStorage.setItem('locale', user.locale);
				if (originalLocalStorage !== user.locale) {
					window.location.reload();
				}
			}

			return user;

		} catch (e) {
			return null;
		}
	}

	hasAccessToken() {
		return localStorage.getItem('access_token') !== null;
	}

	onAccessToken(callback) {
		this.accessTokenCallbacks.push(callback);
	}

	_getPkce() {
		return new PKCE({
			client_id: appConfig.OAUTH.client_id,
			redirect_uri: appConfig.OAUTH.redirect_uri,
			authorization_endpoint: appConfig.OAUTH.authorization_endpoint,
			token_endpoint: appConfig.OAUTH.token_endpoint,
			requested_scopes: appConfig.OAUTH.requested_scopes,
			storage: localStorage
		});
	}

	_persistTokenResponse(resp) {
		if (!resp || !resp.access_token) {
			return false;
		}

		localStorage.setItem('access_token', resp.access_token);
		localStorage.setItem('access_token_expires_at', (new Date()).getTime() + (resp.expires_in * 1000));

		// Only overwrite refresh_token if the server actually returned a new one.
		// Some responses may omit it; storing `undefined` would clobber a working token.
		if (resp.refresh_token) {
			localStorage.setItem('refresh_token', resp.refresh_token);
		}

		this.accessTokenCallbacks.forEach((callback) => {
			callback(resp.access_token);
		});

		this._scheduleProactiveRefresh();

		return true;
	}

	_scheduleProactiveRefresh() {
		if (this._proactiveRefreshTimer) {
			clearTimeout(this._proactiveRefreshTimer);
			this._proactiveRefreshTimer = null;
		}

		const expiresAt = parseInt(localStorage.getItem('access_token_expires_at'), 10);
		if (!expiresAt) {
			return;
		}

		const timeUntilExpiry = expiresAt - (new Date()).getTime();
		// Cap the lead time so it never exceeds half of the remaining lifetime —
		// otherwise short-lived tokens (where lifetime < PROACTIVE_REFRESH_LEAD_MS)
		// would never get a proactive refresh scheduled.
		const leadMs = Math.min(PROACTIVE_REFRESH_LEAD_MS, Math.max(0, timeUntilExpiry / 2));
		const delay = Math.max(0, timeUntilExpiry - leadMs);

		this._proactiveRefreshTimer = setTimeout(() => {
			this._proactiveRefreshTimer = null;
			this.refreshAccessToken().catch(() => {
				// Refresh errors are handled inside refreshAccessToken. The next API
				// call will surface a 401 and trigger the regular re-auth path.
			});
		}, delay);
	}

	/**
	 * Refresh the access token using the stored refresh token. Concurrent callers
	 * share a single in-flight request so we never burn a single-use refresh token
	 * on parallel attempts.
	 *
	 * @returns {Promise<boolean>} true if a fresh access token was obtained.
	 */
	async refreshAccessToken() {
		if (this._refreshInFlight) {
			return this._refreshInFlight;
		}

		const refreshToken = localStorage.getItem('refresh_token');
		if (!refreshToken || refreshToken === 'undefined') {
			return false;
		}

		const pkce = this._getPkce();

		this._refreshInFlight = (async () => {
			try {
				const resp = await pkce.refreshAccessToken(refreshToken);
				return this._persistTokenResponse(resp);
			} catch (e) {
				console.error('Failed to refresh token', e);
				return false;
			} finally {
				this._refreshInFlight = null;
			}
		})();

		return this._refreshInFlight;
	}

	async requireAccessToken() {

		// look for access token
		if (
			localStorage.getItem('access_token') &&
			localStorage.getItem('access_token_expires_at') &&
			localStorage.getItem('access_token_expires_at') > (new Date()).getTime()
		) {
			this._scheduleProactiveRefresh();
			return true;
		}

		// Attempt to refresh token

		if (localStorage.getItem('refresh_token')) {
			if (await this.refreshAccessToken()) {
				return true;
			}
		}

		const pkce = this._getPkce();

		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('error')) {
			alert('Authentication error: ' + urlParams.get('error'));
			return false;
		}

		// Redirect to login
		if (!urlParams.has('code')) {
			const authorizeUrl = pkce.authorizeUrl({ prompt: 'login' });
			window.location.replace(authorizeUrl);
			return false;
		}

		const url = window.location.href;
		const resp = await pkce.exchangeForAccessToken(url);

		if (!this._persistTokenResponse(resp)) {
			return false;
		}

		// Remove code from url
		window.history.replaceState({}, document.title, '/');
		return true;

	}

	async logout() {

		await axios.post(appConfig.API_URL + 'logout', {}, {
			headers: {
				Authorization: 'Bearer ' + localStorage.getItem('access_token')
			}
		});

		this.clearAccessToken(true);
	}

	clearAccessToken(refresh = false) {
		localStorage.removeItem('access_token');
		localStorage.removeItem('access_token_expires_at');
		localStorage.removeItem('refresh_token');
		localStorage.removeItem('organisation_id');

		if (this._proactiveRefreshTimer) {
			clearTimeout(this._proactiveRefreshTimer);
			this._proactiveRefreshTimer = null;
		}

		// Also destroy the credentials of the file pickers (Google Drive, OneDrive)
		FilePickerCredentials.clear();

		if (refresh) {
			window.location = '/';
		}
	}

}

const auth = new Authentication();

export { auth };
